import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  ReactNode,
} from "react";
import { Popover, Whisper } from "rsuite";
import { getTextColor } from "../../utils/DashboardUtils";
import { Button, Col, Row } from "reactstrap";
import { useAtomValue, useSetAtom } from "jotai";
import SelectablePopover from "../../components/Records/References/SelectablePopover";
import AppTooltip from "../../elements/AppTooltip";
import useGlobalLists from "../../api/api-hooks/cloud/useGlobalLists";
import { selectableOpen } from "../../atoms/GlobalAtoms";
import useMappedOptions from "./useMappedOptions";
import useMappedValue from "./useMappedValue";
import SelectableOptionsMenu from "./SelectableOptionsMenu";
import { IsMobileAtom } from "../../App";
import AppSpinner from "../../elements/AppSpinner";
import EmptyField from "../Records/EmptyField";
import { OverlayTriggerType } from "rsuite/esm/internals/Overlay/OverlayTrigger";

interface GroupByLabel {
  from: string;
  to: string;
}

interface Option {
  [key: string]: any;
}

export interface SelectableInputProps {
  table?: boolean;
  options?: Option[];
  empty?: ReactNode;
  labelKey?: string;
  valueKey?: string;
  colorKey?: string;
  value?: Option | Option[] | null;
  onSelect?: (option: Option) => void;
  onDeselect?: (option: Option) => void;
  onClear?: () => void;
  clearable?: boolean;
  loadingOptions?: boolean;
  size?: string;
  multiselect?: boolean;
  disabled?: boolean;
  mode?: "rw" | "r";
  inputDisplayMode?: "input" | "subtle";
  listDisplayMode?: "list" | "grid";
  groupBy?: string;
  nestedGroupBy?: string;
  ungroupedOptionLabel?: string;
  maxValuesDisplayed?: number | null;
  field?: { Name?: string } | null;
  sort?: boolean;
  sortGroups?: boolean;
  groupByLabels?: GroupByLabel[];
  externalOpen?: boolean;
  setExternalOpen?: (open: boolean) => void;
  modifyTrigger?: "click" | "doubleclick";
  tooltipTrigger?: OverlayTriggerType;
  save?: () => void;
  addableGroups?: any;
  hiddenTemporarily?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  searchable?: boolean;
}

export interface MultiSelectValueProps {
  value: Option;
  className?: string;
  fullWidth?: boolean;
  labelKey?: string;
  colorKey?: string;
  defaultBackgroundColor: string;
  inputDisplayMode?: "input" | "subtle";
  mode?: "rw" | "r";
}

const emptyArr: GroupByLabel[] = [];

const SelectableInput = (
  {
    table = false,
    options = [],
    empty = "--",
    labelKey = "label",
    valueKey = "value",
    colorKey = "color",
    value,
    onSelect,
    onDeselect,
    onClear,
    clearable = true,
    loadingOptions = false,
    multiselect = false,
    disabled = false,
    mode = "rw",
    inputDisplayMode = "input",
    listDisplayMode = "list",
    groupBy = "",
    nestedGroupBy = "",
    ungroupedOptionLabel = "Other",
    maxValuesDisplayed = null,
    field = null,
    sort = true,
    sortGroups = true,
    groupByLabels = emptyArr,
    externalOpen = false,
    setExternalOpen,
    modifyTrigger = "click",
    tooltipTrigger = "hover",
    save,
    addableGroups,
    hiddenTemporarily = false,
    onOpen,
    onClose,
    searchable = true,
  }: SelectableInputProps,
  ref: React.Ref<{ focus: () => void }>
) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const defaultBackgroundColor = "#e8edea"; // var(--superlight)
  const editMenuRef = useRef<any>(null);
  const infoWhisperRef = useRef<any>(null);
  const { globalLists } = useGlobalLists();

  const [openEditorLocal, toggleOpenEditorLocal] = useState<boolean>(false);
  const [attemptClear, setAttemptClear] = useState(false);

  const openEditor = externalOpen ? externalOpen : openEditorLocal;
  const toggleOpenEditor = !externalOpen
    ? toggleOpenEditorLocal
    : setExternalOpen;

  useEffect(() => {
    if (externalOpen) {
      startEditing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const mappedOptions = useMappedOptions({
    options,
    globalLists,
    groupBy,
    sort,
    labelKey,
    ungroupedOptionLabel,
    groupByLabels,
    sortGroups,
    nestedGroupBy,
    addableGroups,
  });

  const mappedValue = useMappedValue({
    multiselect,
    options,
    value,
    labelKey,
    valueKey,
    mappedOptions,
  });

  const inputStyles = useMemo<React.CSSProperties>(() => {
    let styles: React.CSSProperties = {
      position: "relative",
      width: "100%",
      height: "calc( 0.45em + 1.25rem + 5px)",
      cursor: mode === "rw" ? "pointer" : "auto",
    };

    if (disabled) {
      styles.cursor = "not-allowed";
    }

    return styles;
  }, [mode, disabled]);

  const setSelectableOpen = useSetAtom(selectableOpen);

  const startEditing = () => {
    editMenuRef.current?.open();
    setSelectableOpen(true);
    infoWhisperRef.current?.close();
    toggleOpenEditor && toggleOpenEditor(true);
  };

  const stopEditing = () => {
    editMenuRef.current?.close();
    setSelectableOpen(false);
    toggleOpenEditor && toggleOpenEditor(false);
  };

  const handleOpen = () => {
    if (!openEditor) {
      return startEditing();
    }

    stopEditing();
  };

  useImperativeHandle(ref, () => ({
    focus: () => handleOpen(),
  }));

  const handleTriggerClick = () => {
    if (modifyTrigger === "click") {
      if (mode !== "rw") {
        return;
      }
      handleOpen();
    }
  };

  const handleDoubleClick = () => {
    if (modifyTrigger === "doubleclick") {
      if (mode !== "rw") {
        return;
      }
      handleOpen();
    }
  };

  return (
    <Whisper
      enterable={true}
      preventOverflow
      ref={editMenuRef}
      placement="autoVerticalEnd"
      trigger={
        openEditor && !attemptClear && !hiddenTemporarily ? "click" : "none"
      }
      onOpen={onOpen}
      onClose={() => {
        if (save) save();
        onClose && onClose();
      }}
      speaker={
        <Popover
          className="sidebar-popover p-0"
          style={{
            width: "350px",
            display: hiddenTemporarily ? "none" : "unset",
          }}
        >
          <div>
            {loadingOptions ? (
              <>
                <div className="p-2 border-bottom border-superlight">
                  <Row className="align-items-center">
                    <Col xs="">
                      <h5 className="m-0 text-dark">
                        {field?.Name ? field?.Name : "Edit"}
                      </h5>
                    </Col>
                    <Col xs="auto">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          stopEditing();
                        }}
                      >
                        Done
                      </Button>
                    </Col>
                  </Row>
                </div>
                <div
                  style={{
                    maxHeight: 350,
                    overflowX: "hidden",
                    overflowY: "auto",
                  }}
                >
                  <div className="p-3" style={{ height: 200 }}>
                    <div
                      className="text-center"
                      style={{
                        position: "relative",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <div>
                        <AppSpinner />
                      </div>
                      <p style={{ fontSize: 14 }} className="mb-0 mt-3">
                        Fetching options...
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {!disabled && (
                  <SelectableOptionsMenu
                    groupBy={groupBy}
                    nestedGroupBy={nestedGroupBy}
                    options={mappedOptions}
                    multiselect={multiselect}
                    value={value}
                    listDisplayMode={listDisplayMode}
                    onSelect={(opt: Option) => {
                      if (typeof onSelect === "function") {
                        onSelect(opt);

                        if (!multiselect) {
                          setTimeout(() => stopEditing());
                        }
                      }
                    }}
                    onDeselect={(opt: Option) => {
                      onDeselect && onDeselect(opt);
                    }}
                    labelKey={labelKey}
                    colorKey={colorKey}
                    valueKey={valueKey}
                    addableGroups={addableGroups}
                    clearable={clearable}
                    stopEditing={stopEditing}
                    setAttemptClear={setAttemptClear}
                    onClear={onClear}
                    searchable={searchable}
                    field={undefined}
                  />
                )}
              </>
            )}
          </div>
        </Popover>
      }
    >
      <div
        onClick={handleTriggerClick}
        onDoubleClick={handleDoubleClick}
        className="w-100 show-on-hover"
      >
        {(multiselect && Array.isArray(value) && value.length) ||
        (!multiselect && value) ? (
          <>
            <AppTooltip
              placement="auto"
              trigger={
                openEditor ? "none" : multiselect ? tooltipTrigger : "none"
              }
              delayOpen={400}
              externalRef={infoWhisperRef}
              content={
                multiselect && !openEditor ? (
                  <SelectablePopover
                    options={value as Option[]}
                    groupBy={groupBy}
                    sortBy={labelKey}
                    groupByLabels={groupByLabels}
                  />
                ) : null
              }
            >
              <div
                style={inputStyles}
                className={`text-truncate ${
                  inputDisplayMode === "input" ? "form-control p-1" : ""
                }`}
              >
                <div>
                  <div
                    style={{
                      width: !disabled ? "100%" : "100%",
                      overflow: "hidden",
                      transition: "width 0.03s ease-in-out",
                    }}
                  >
                    {multiselect ? (
                      <>
                        {mappedValue &&
                        Array.isArray(mappedValue) &&
                        mappedValue.length ? (
                          <>
                            {maxValuesDisplayed &&
                            mappedValue.length > maxValuesDisplayed ? (
                              <>
                                {mappedValue
                                  .slice(0, maxValuesDisplayed)
                                  .map((val, i) => (
                                    <MultiSelectValue
                                      key={i}
                                      value={val}
                                      className={
                                        i !== mappedValue.length - 1
                                          ? "mr-1"
                                          : ""
                                      }
                                      labelKey={labelKey}
                                      colorKey={colorKey}
                                      defaultBackgroundColor={
                                        defaultBackgroundColor
                                      }
                                      inputDisplayMode={inputDisplayMode}
                                      mode={mode}
                                    />
                                  ))}
                                <div
                                  className="text-medium d-inline-block ml-1 font-weight-500"
                                  style={{
                                    fontSize:
                                      inputDisplayMode === "input" ? 13 : 14,
                                    position: "relative",
                                    top: inputDisplayMode === "input" ? -5 : -7,
                                  }}
                                >
                                  + more
                                </div>
                              </>
                            ) : (
                              <>
                                {mappedValue.map((val, i) => (
                                  <MultiSelectValue
                                    key={i}
                                    value={val}
                                    className={
                                      i !== mappedValue.length - 1 ? "mr-1" : ""
                                    }
                                    labelKey={labelKey}
                                    colorKey={colorKey}
                                    defaultBackgroundColor={
                                      defaultBackgroundColor
                                    }
                                    inputDisplayMode={inputDisplayMode}
                                    mode={mode}
                                  />
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <div className="text-dark d-inline-block mr-2">
                            {empty}
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          width:
                            inputDisplayMode !== "input"
                              ? "calc(100% - 15px)"
                              : "100%",
                          overflow: "hidden",
                          transition: "width 0.03s ease-in-out",
                        }}
                      >
                        <MultiSelectValue
                          fullWidth={true}
                          value={mappedValue as Option}
                          className="text-truncate"
                          labelKey={labelKey}
                          colorKey={colorKey}
                          defaultBackgroundColor={defaultBackgroundColor}
                          inputDisplayMode={inputDisplayMode}
                          mode={mode}
                        />
                      </div>
                    )}
                  </div>

                  {mode === "rw" && !disabled && (
                    <div
                      style={{
                        float: "right",
                        position: "absolute",
                        right:
                          inputDisplayMode === "input" ? "0.25rem" : "0rem",
                        top:
                          inputDisplayMode === "input" ? "0.2rem" : "0.25rem",
                        height: "100%",
                        borderRadius: 5,
                        marginLeft: 5,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpen();
                      }}
                    >
                      <i
                        className="mdi mdi-pencil show-on-hover-child pencil-hover"
                        style={{
                          fontSize: 18,
                          position: "relative",
                          top: 0,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </AppTooltip>
          </>
        ) : (
          <div
            style={inputStyles}
            className={`text-truncate show-on-hover ${
              inputDisplayMode === "input" ? "form-control p-1" : ""
            }`}
          >
            <div>
              <div
                style={{
                  width: !disabled ? "calc(100% - 25px)" : "100%",
                  overflow: "hidden",
                  transition: "width 0.03s ease-in-out",
                  paddingTop: inputDisplayMode === "input" ? 1 : 6,
                }}
                className={`${table ? "" : "pl-0 pr-4 mr-2"}`}
              >
                {empty === "--" ? <EmptyField table={table} /> : empty}
              </div>

              {mode === "rw" && !disabled && (
                <div
                  style={{
                    float: "right",
                    position: "absolute",
                    right: "0.25rem",
                    top: inputDisplayMode === "input" ? "0.2rem" : "0.25rem",
                  }}
                  onClick={handleOpen}
                >
                  {mobileScreen ? (
                    <i
                      style={{
                        borderRadius: 5,
                        right: 2,
                        paddingLeft: 6,
                        color: "var(--medium)",
                        fontSize: "14px",
                        opacity: 0,
                      }}
                      className="mdi mdi-pencil cursor-pointer bg-white d-flex justify-content-center align-items-center m-0 p-0"
                    />
                  ) : (
                    <i
                      className="mdi mdi-pencil show-on-hover-child pencil-hover"
                      style={{
                        fontSize: 18,
                        position: "relative",
                        top: 0,
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Whisper>
  );
};

const ForwardedSelectableInput = forwardRef(SelectableInput);
export default ForwardedSelectableInput;

export function MultiSelectValue({
  value,
  className = "",
  fullWidth = false,
  labelKey = "label",
  colorKey = "color",
  defaultBackgroundColor,
  inputDisplayMode = "input",
  mode = "rw",
}: MultiSelectValueProps) {
  const textColor = getTextColor(
    value?.[colorKey] ? value[colorKey] : defaultBackgroundColor
  );
  const color =
    value?.[colorKey] && !String(value[colorKey]).includes("#")
      ? "#" + value[colorKey]
      : value?.[colorKey];

  return (
    <div
      className={`text-truncate px-1 d-inline-block font-weight-500 ${className}`}
      style={{
        maxWidth: fullWidth ? "auto" : 120,
        width: fullWidth ? "100%" : "auto",
        background: value?.[colorKey] ? color : defaultBackgroundColor,
        color: textColor,
        borderRadius: 3,
        fontSize: 13,
        height:
          inputDisplayMode === "input"
            ? "auto"
            : "calc( 0.45em + 1.25rem - 2px)",
        paddingTop: inputDisplayMode === "input" ? "0px" : "2px",
        marginTop: inputDisplayMode === "input" ? "0px" : "5px",
        transition: "all 0.05s ease-in-out",
        cursor: mode === "rw" ? "pointer" : "auto",
      }}
    >
      {value?.icon && (
        <i
          className={`${value.icon} mr-1`}
          style={{ position: "relative" }}
        ></i>
      )}
      {value?.[labelKey]}
    </div>
  );
}
